import { useEffect, useState } from "react";
import { PackageCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { RECEIVING_ROLES } from "@/constants/roles";
import DocumentsPanel from "@/components/DocumentsPanel";
import { fmtDateTime } from "@/lib/utils";
import SearchableSelect from "@/components/SearchableSelect";

const CONDITIONS = [
  { value: "ok", label: "OK" },
  { value: "damaged", label: "Damaged" },
  { value: "wrong_item", label: "Wrong Item" },
];

const emptyLineState = (items) =>
  items.map((it) => ({ qty: "", condition: "ok" }));

export default function ReceivingPanel({ po, onUpdate }) {
  const [history, setHistory] = useState(null);
  const [lineState, setLineState] = useState(() => emptyLineState(po.items));
  const [missingNotes, setMissingNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();

  const loadHistory = () => api.get(`/contractor/purchase-orders/${po.id}/receiving-history`).then((r) => setHistory(r.data));
  useEffect(() => { loadHistory(); setLineState(emptyLineState(po.items)); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [po.id]);

  const canReceive = RECEIVING_ROLES.includes(user?.role);
  const remaining = (i) => Math.max(0, (po.items[i].qty || 0) - (po.items[i].received_qty || 0));
  const openLines = po.items.map((_, i) => i).filter((i) => remaining(i) > 0);

  const setLine = (i, patch) => setLineState((s) => s.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));

  const submit = async () => {
    const lines = openLines
      .map((i) => ({ line_index: i, qty_received: Number(lineState[i]?.qty || 0), condition: lineState[i]?.condition || "ok" }))
      .filter((l) => l.qty_received > 0);
    if (lines.length === 0) return toast.error("Enter a received quantity for at least one line");
    for (const l of lines) {
      if (l.qty_received > remaining(l.line_index)) return toast.error(`Line ${l.line_index + 1}: quantity exceeds remaining`);
    }
    setSaving(true);
    try {
      await api.post(`/contractor/purchase-orders/${po.id}/receive`, { lines, missing_notes: missingNotes || null });
      toast.success("Receipt recorded");
      setLineState(emptyLineState(po.items));
      setMissingNotes("");
      loadHistory();
      onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to record receipt");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-8" data-testid="receiving-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[14.5px] font-bold">Receiving</div>
        {po.needs_credit && (
          <span className="flex items-center gap-1 ss-pill ss-pill-red" data-testid="needs-credit-flag">
            <AlertTriangle size={11} /> Pending Credit
          </span>
        )}
      </div>

      {canReceive && openLines.length > 0 ? (
        <div className="border border-slate-200 rounded-xl p-4 space-y-4" data-testid="receiving-form">
          <div className="space-y-3">
            {openLines.map((i) => {
              const it = po.items[i];
              return (
                <div key={i} className="border border-slate-100 rounded-lg p-3" data-testid={`receiving-line-${i}`}>
                  <div className="text-[13px] font-medium">{it.description}</div>
                  <div className="text-[11.5px] text-slate-500 mt-0.5">
                    Ordered {it.qty} {it.uom || it.unit} · Received so far {it.received_qty || 0} · Remaining {remaining(i)}
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <input
                      type="number" min="0" max={remaining(i)} step="0.01"
                      value={lineState[i]?.qty ?? ""}
                      onChange={(e) => setLine(i, { qty: e.target.value })}
                      placeholder="Qty received"
                      data-testid={`receiving-qty-input-${i}`}
                      className="h-11 px-3 rounded-lg border border-slate-200 text-[15px]"
                    />
                    <SearchableSelect
                      value={lineState[i]?.condition ?? "ok"}
                      onChange={(e) => setLine(i, { condition: e.target.value })}
                      data-testid={`receiving-condition-select-${i}`}
                      className="h-11 px-3 rounded-lg border border-slate-200 text-[15px]"
                    >
                      {CONDITIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </SearchableSelect>
                  </div>
                </div>
              );
            })}
          </div>
          <textarea
            value={missingNotes}
            onChange={(e) => setMissingNotes(e.target.value)}
            placeholder="Notes on missing or damaged items (optional)"
            rows={2}
            data-testid="receiving-missing-notes-input"
            className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-[15px]"
          />
          <button
            disabled={saving} onClick={submit} data-testid="submit-receiving-button"
            className="w-full h-12 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-[14px] font-semibold disabled:opacity-60 flex items-center justify-center gap-2"
          >
            <PackageCheck size={16} /> {saving ? "Recording…" : "Record Receipt"}
          </button>
        </div>
      ) : openLines.length === 0 ? (
        <div className="text-[13px] text-slate-400">All lines fully received.</div>
      ) : null}

      <div className="mt-6">
        <div className="text-[13px] font-semibold text-slate-700 mb-2">Receiving History</div>
        <div className="space-y-3" data-testid="receiving-history">
          {history && history.length === 0 && <div className="text-slate-400 text-[13px]">No deliveries received yet.</div>}
          {(history || []).map((ev) => (
            <div key={ev.id} className="border border-slate-200 rounded-xl p-3" data-testid={`receiving-event-${ev.id}`}>
              <div className="flex items-center gap-2 text-[12.5px]">
                <span className="font-semibold">{ev.received_by_name}</span>
                <span className="text-slate-400">· {fmtDateTime(ev.created_at)}</span>
              </div>
              <div className="mt-1.5 space-y-1">
                {ev.lines.map((l, i) => (
                  <div key={i} className="text-[12.5px] text-slate-600 flex items-center gap-2">
                    <span>{po.items[l.line_index]?.description || `Line ${l.line_index + 1}`}: {l.qty_received}</span>
                    {l.condition !== "ok" && (
                      <span className="ss-pill ss-pill-red">{l.condition === "damaged" ? "Damaged" : "Wrong Item"}</span>
                    )}
                  </div>
                ))}
              </div>
              {ev.missing_notes && <div className="text-[12px] text-slate-500 mt-1 italic">"{ev.missing_notes}"</div>}
              <DocumentsPanel entityType="receiving" entityId={ev.id} title="Photos" compact />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
